import * as React from 'react';
import { animated, useTransition } from '@react-spring/web';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { getDeployment } from '@/redux/modules/config';
import { getItemSummariesForCatalog } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveCatalogCurrentItemId } from '@/redux/modules/console';
import { ItemSummary } from '@/types/item/ItemSummary';
import { useAppSelector } from '@/redux/hooks';
import { useCalculatedRelativeFavoritesPosition } from './hooks/useCalculatedRelativeFavoritesPosition';
import { useFindLiveItemIndex } from './hooks/useFindLiveItemIndex';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import ListItem from './components/ListItem/ListItem';
import styled, { css } from 'styled-components';

type Props = {
    catalogId: number;
};

const AuctionLotsScroller = ({ catalogId }: Props) => {
    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const items = useAppSelector((state) => getItemSummariesForCatalog(state, catalogId));
    const deployment = useAppSelector(getDeployment);
    const { formatMessage } = useIntl();

    // sometimes the itemSummary index isn't the same as it's position in the list so we need to find it manually
    const liveItemIndex = useFindLiveItemIndex({ catalogId, itemId });
    const [isLiveItemVisible, setIsLiveItemVisible] = React.useState<boolean>(true);

    // Used to see if it should autoscroll
    const [indexDifference, setIndexDifference] = React.useState<number>(liveItemIndex);

    const lotsUntilUpcomingFavorites = useCalculatedRelativeFavoritesPosition({
        catalogId,
        itemId,
    });

    const virtuoso = React.useRef<VirtuosoHandle>(null);

    const handleScrollToLiveItem = React.useCallback(() => {
        virtuoso?.current?.scrollToIndex?.({
            align: 'start',
            behavior: 'smooth',
            index: liveItemIndex,
        });
    }, [liveItemIndex]);

    // Handle if react-virtuoso scrolls, track if live item is visible or not
    const handleOnRangeChanged = ({ endIndex, startIndex }) => {
        if (liveItemIndex >= startIndex && liveItemIndex <= endIndex) {
            setIsLiveItemVisible(true);
        } else {
            setIsLiveItemVisible(false);
            setIndexDifference(liveItemIndex);
        }
    };

    const backToLiveButtonTransition = useTransition(!isLiveItemVisible, {
        enter: { opacity: 1 },
        from: { opacity: 0 },
        leave: { opacity: 0 },
    });

    React.useEffect(() => {
        if (isLiveItemVisible && indexDifference !== liveItemIndex) {
            handleScrollToLiveItem();
            setIndexDifference(liveItemIndex);
        }
    }, [liveItemIndex, isLiveItemVisible, handleScrollToLiveItem, indexDifference]);

    return (
        <Container
            $addBorderTop={!isLiveItemVisible}
            data-testid="auctionLotScroller"
        >
            {backToLiveButtonTransition(
                (styles, item) =>
                    item && (
                        <BackToLiveAnimatedContainer style={styles}>
                            <Button
                                onClick={() => handleScrollToLiveItem()}
                                variant="outlined"
                            >
                                {formatMessage({
                                    id: 'bidding_console.console.button.backToLiveItem',
                                })}
                            </Button>
                        </BackToLiveAnimatedContainer>
                    )
            )}
            <Virtuoso
                data={items}
                // Used for tests
                data-testid="virtuosoScrollList"
                initialItemCount={deployment === 'test' ? 4 : undefined}
                initialTopMostItemIndex={liveItemIndex}
                itemContent={(_index, item: ItemSummary) => (
                    <ListItem
                        index={_index}
                        isLiveItemVisible={isLiveItemVisible}
                        itemId={item.itemId}
                        liveItemId={itemId}
                        liveItemIndex={liveItemIndex}
                        lotsUntilUpcoming={lotsUntilUpcomingFavorites[itemId]}
                    />
                )}
                rangeChanged={handleOnRangeChanged}
                ref={virtuoso}
            />
        </Container>
    );
};

export default AuctionLotsScroller;

const Container = styled.div<{ $addBorderTop: boolean }>`
    background: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    overflow: hidden;
    height: 100%;
    position: relative;
    ${({ $addBorderTop }) =>
        $addBorderTop &&
        css`
            border-top: unset;
        `}
`;

const BackToLiveAnimatedContainer = styled(animated.div)`
    position: absolute;
    top: 1;
    left: 0;
    right: 0;

    /** Overlay must stay above favorite item buttons (z-index: 2) */
    z-index: 3;
    background: ${({ theme }) => theme.colors.white};
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    display: flex;
    align-items: center;
    padding: 24px;
    padding-top: 1px;

    button {
        width: 100%;
    }
`;
